import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslations } from "next-intl";
import { apiFetch } from "@/services/http/api-fetch";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { useEffect, useRef, useState } from "react";

export function TenantVerifyEmailPage() {
  const t = useTranslations("auth");
  const tRoot = useTranslations();
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const missingToken = !token;
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    missingToken ? "error" : "loading",
  );
  const [message, setMessage] = useState<string | null>(
    missingToken ? t("verifyLinkInvalid") : null,
  );

  // The token is one-time: a second POST is always rejected. StrictMode and re-renders
  // can re-run this effect, and whichever response lands last would win, so send each
  // token once. A ref survives both.
  const submittedToken = useRef<string | null>(null);

  useEffect(() => {
    if (missingToken || submittedToken.current === token) return;
    submittedToken.current = token;

    void (async () => {
      // AUTH-12: every outcome — network failure, a non-JSON error page — ends "Verifying…".
      try {
        const res = await apiFetch("/v1/auth/verify-email", {
          method: "POST",
          body: JSON.stringify({ token, tenantSlug }),
        });
        if (!res.ok) throw await readApiErrorResponse(res);
        setStatus("success");
        setMessage(t("emailVerified"));
      } catch (err) {
        setStatus("error");
        setMessage(apiErrorText(err, tRoot, "auth.verifyFailed"));
      }
    })();
  }, [missingToken, t, tRoot, token, tenantSlug]);

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4">
      <h1 className="mb-2 text-2xl font-semibold">{t("verifyEmailTitle")}</h1>
      <p className="mb-6 text-sm text-[var(--text-muted)]">
        {t("tenantLabel", { tenant: tenantSlug })}
      </p>
      {status === "loading" && (
        <p className="text-sm text-[var(--text-muted)]" data-testid="verify-email-loading">
          {t("verifying")}
        </p>
      )}
      {status === "success" && (
        <p className="text-sm text-green-700" data-testid="verify-email-success">
          {message}
        </p>
      )}
      {status === "error" && (
        <p className="text-sm text-red-600" data-testid="verify-email-error">
          {message}
        </p>
      )}
      <p className="mt-4 text-center text-sm">
        <Link to={`/t/${tenantSlug}/login`} className="text-[var(--text-heading)] underline">
          {t("goToSignIn")}
        </Link>
      </p>
    </main>
  );
}
